import isProblemDetails from '../lib/isProblemDetails';
import isAuthError from '../lib/isAuthError';
import ProblemDetailsError from './ProblemDetailsError';
import {ProblemDetails} from '../types';
import {handlePaymentRequired} from './subscriptionEnforcement';

export const handleResponseError = async (response: Response) => {
    const statusCode = response.status;
    const contentType = response.headers.get('content-type') || '';

    // Create a base problem details object
    const problemDetails: ProblemDetails = {
        type: 'https://tools.ietf.org/html/rfc7231',
        title: getDefaultErrorMessage(statusCode),
        status: statusCode,
        detail: null,
        instance: response.url || null,
        reasonPhrase: response.statusText || null
    };

    // Handle different content types
    if (
        contentType.includes('application/json') ||
        contentType.includes('application/problem+json')
    ) {
        try {
            const errorData = await response.json();

            // Subscription enforcement (402): redirect to billing or show a
            // read-only toast, then continue throwing so callers still see
            // the error (and optimistic updates revert).
            if (statusCode === 402) {
                handlePaymentRequired(errorData);
            }

            // If response is already a problem details object, use it
            if (isProblemDetails(errorData)) {
                throw new ProblemDetailsError(errorData);
            }

            // OAuth-style auth error: { error, error_description }.
            // Surface error_description as the user-facing detail and stash the
            // original under `authError` so callers (e.g. LoginPage) can branch
            // on specific error codes like 'unconfirmed_email'.
            if (isAuthError(errorData)) {
                problemDetails.detail = errorData.error_description;
                problemDetails.authError = errorData;
                throw new ProblemDetailsError(problemDetails);
            }

            // Otherwise, enhance our problem details with any information from the response
            if (errorData.message) {
                problemDetails.detail = errorData.message;
            }

            // Add any additional properties from errorData to the problem details
            Object.entries(errorData).forEach(([key, value]) => {
                if (
                    !['type', 'title', 'status', 'detail', 'instance'].includes(
                        key
                    )
                ) {
                    problemDetails[key] = value;
                }
            });
        } catch (error) {
            if (error instanceof ProblemDetailsError) {
                throw error; // Re-throw ProblemDetailsError immediately
            }
            // On parsing error, try to get text content
            const textContent = await response.clone().text();
            problemDetails.detail = textContent || problemDetails.title;
        }
    } else if (contentType.includes('text/html')) {
        // Handle HTML content
        const htmlContent = await response.text();
        problemDetails.detail =
            'The server returned an error response. Click [View Response] for details';
        problemDetails.html = htmlContent; // Add HTML content to the additional properties
    } else {
        // Handle text or other content types
        const textContent = await response.text();
        problemDetails.detail = textContent || problemDetails.title;
    }

    throw new ProblemDetailsError(problemDetails);
};

/**
 * Returns a default error message based on the HTTP status code
 */
function getDefaultErrorMessage(statusCode: number): string {
    switch (statusCode) {
        case 400:
            return 'Bad request.';
        case 401:
            return 'Unauthorized.';
        case 402:
            return 'Subscription required.';
        case 403:
            return 'Forbidden.';
        case 404:
            return 'Not found.';
        case 405:
            return 'Method not allowed.';
        case 408:
            return 'Request timeout.';
        case 409:
            return 'Conflict.';
        case 413:
            return 'Payload too large. Please reduce the size of your request.';
        case 429:
            return 'Too many requests. Please try again later.';
        case 500:
            return 'Internal server error. Please try again later.';
        case 502:
            return 'Bad gateway.';
        case 503:
            return 'Service unavailable. Please try again later.';
        case 504:
            return 'Gateway timeout.';
        case 524:
            return 'A timeout occurred.';
        default:
            return `HTTP Error ${statusCode}`;
    }
}
